import type { QuoteRevisionSnapshot } from "../../Submission/types/revision.type";
import type { RfxQuoteItem } from "../../Submission/types/quote.type";
import type { QuoteTableRow } from "./quote-table.types";

/**
 * Returns a Set of field keys (e.g. "q-123.quantity", "q-123.rate") that differ
 * between two revisions.
 */
export const getQuoteDiff = (
	a: QuoteRevisionSnapshot,
	b: QuoteRevisionSnapshot
): Set<string> => {
	const diffKeys = new Set<string>();

	const aAnswerMap = new Map(
		a.answers.map((ans) => [ans.rfq_quote_item_id, ans])
	);
	const bAnswerMap = new Map(
		b.answers.map((ans) => [ans.rfq_quote_item_id, ans])
	);

	const allItemIds = new Set([...aAnswerMap.keys(), ...bAnswerMap.keys()]);

	let aSubtotal = 0;
	let bSubtotal = 0;

	allItemIds.forEach((itemId) => {
		const aAns = aAnswerMap.get(itemId);
		const bAns = bAnswerMap.get(itemId);

		if (!aAns || !bAns) {
			diffKeys.add(`q-${itemId}.quantity`);
			diffKeys.add(`q-${itemId}.rate`);
			diffKeys.add(`q-${itemId}.cost`);
			if (aAns) {
				aSubtotal +=
					Number(aAns.answer.value.quantity) * Number(aAns.answer.value.rate);
			}
			if (bAns) {
				bSubtotal +=
					Number(bAns.answer.value.quantity) * Number(bAns.answer.value.rate);
			}
			return;
		}

		const aQty = Number(aAns.answer.value.quantity);
		const bQty = Number(bAns.answer.value.quantity);
		const aRate = Number(aAns.answer.value.rate);
		const bRate = Number(bAns.answer.value.rate);

		aSubtotal += aQty * aRate;
		bSubtotal += bQty * bRate;

		if (aQty !== bQty) {
			diffKeys.add(`q-${itemId}.quantity`);
		}
		if (aRate !== bRate) {
			diffKeys.add(`q-${itemId}.rate`);
		}
		if (aQty * aRate !== bQty * bRate) {
			diffKeys.add(`q-${itemId}.cost`);
		}
	});

	if (aSubtotal !== bSubtotal) {
		diffKeys.add("total.subtotal");
		diffKeys.add("total.gst");
		diffKeys.add("total.totalcost");
	}

	return diffKeys;
};

/**
 * Formats a revision number for display.
 * 0 → 'Original', N → 'Revision N'
 */
export const formatRevisionLabel = (revision: number): string => {
	if (revision === 0) {
		return "Original";
	}
	return `Revision ${revision}`;
};

/**
 * Formats a number as AUD currency.
 */
export const formatCurrency = (value: number): string =>
	value.toLocaleString("en-AU", { style: "currency", currency: "AUD" });

/**
 * Total row types used by QuoteTableEdit and ReadOnlyQuoteTable.
 */
export enum TotalType {
	GST = "gst",
	SubTotal = "subtotal",
	Total = "totalcost",
}

export const SUBTOTAL_ROW = {
	id: TotalType.SubTotal,
	label: "Subtotal",
	color: "#475467",
} as const;

export const TOTAL_ROWS = [
	SUBTOTAL_ROW,
	{ id: TotalType.GST, label: "GST (10%)", color: "#475467" },
	{ id: TotalType.Total, label: "Total Cost", color: "#101828" },
] as const;

/**
 * Calculates subtotal, GST, or total cost from an array of QuoteTableRows.
 * When rows have quantityCategories (value columns), total per row is:
 *   quantity × sum(value column rates). If no quantity, sum of rates.
 * Falls back to simple quantity × rate for rows without value columns.
 */
export const calculateTotalFromRows = (
	rows: QuoteTableRow[],
	type: TotalType
): number => {
	const subtotal = rows.reduce((acc, row) => {
		if (
			row.quantityCategories &&
			Object.keys(row.quantityCategories).length > 0
		) {
			const valueSum = Object.values(row.quantityCategories).reduce(
				(sum, cat) => sum + Number(cat.rate || 0),
				0
			);
			return acc + (row.quantity ? row.quantity * valueSum : valueSum);
		}
		return acc + row.quantity * row.rate;
	}, 0);
	switch (type) {
		case TotalType.SubTotal:
			return subtotal;
		case TotalType.GST:
			return subtotal * 0.1;
		case TotalType.Total:
			return subtotal + subtotal * 0.1;
		default:
			return 0;
	}
};

/**
 * Triggers a browser download for a Blob.
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};

/**
 * Converts a QuoteRevisionSnapshot's answers into QuoteTableRow[].
 * When `quoteItems` is provided, item names and unit labels are resolved
 * from the quote definition rather than showing generic "Item N".
 */
export const mapSnapshotToRows = (
	snapshot: QuoteRevisionSnapshot,
	quoteItems?: RfxQuoteItem[]
): QuoteTableRow[] => {
	const itemMap = quoteItems
		? new Map(quoteItems.map((item) => [item.id, item]))
		: undefined;

	return snapshot.answers
		.map((ans) => {
			const item = itemMap?.get(ans.rfq_quote_item_id);
			return {
				itemId: ans.rfq_quote_item_id,
				name: item?.name ?? `Item ${ans.rfq_quote_item_id}`,
				unitLabel: item?.unitMeasurement,
				quantity: Number(ans.answer.value.quantity),
				rate: Number(ans.answer.value.rate),
				sort_order: Number(item?.sortOrder),
			};
		})
		.sort((a, b) => a.sort_order - b.sort_order);
};
